/**
 * Estados e alíquotas (`/estados`) — a tabela fiscal usada no cálculo de toda
 * proposta. Leitura livre para qualquer pessoa logada; edição só para
 * administradores (`PATCH/POST/DELETE /states`).
 *
 * Alíquotas chegam da API como fração decimal (`0.07`) e são sempre exibidas
 * e digitadas como percentual (`7,00`) — a conversão mora só nas duas funções
 * no topo deste arquivo; o resto da tela nunca vê a fração crua fora delas.
 */
import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useCreateState, useDeleteState, useStates, useUpdateState } from '@/hooks/useAdmin';
import { ApiError } from '@/lib/api';
import type { CreateStateInput, StateTax, UpdateStateInput } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Percent } from '@/components/Percent';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import './Admin.css';

/** `0.0325` → `"3,25"` — só para preencher o campo de edição. */
function fractionToPercentInput(value: number): string {
  return (value * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** `"3,25"` (ou `"3.25"`) → `0.0325` — só ao montar o corpo do PATCH/POST. */
function percentInputToFraction(raw: string): number {
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
  const n = Number(normalized);
  return Number.isFinite(n) ? n / 100 : 0;
}

const CONSEQUENCE_NOTE =
  'Mudar uma alíquota vale só para propostas novas — as já emitidas mantêm o retrato das alíquotas no momento em que foram geradas.';

export function Estados() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const toast = useToast();
  const statesQuery = useStates();
  const createState = useCreateState();
  const updateState = useUpdateState();
  const deleteState = useDeleteState();

  const [modalState, setModalState] = useState<StateTax | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StateTax | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteState.mutateAsync(deleteTarget.uf);
      toast.success(`${deleteTarget.nome} foi removido.`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível excluir agora.');
    } finally {
      setDeleteBusy(false);
    }
  }

  const columns: DataTableColumn<StateTax>[] = [
    { key: 'uf', header: 'UF', render: (row) => <span className="mono">{row.uf}</span> },
    { key: 'nome', header: 'Nome', render: (row) => row.nome },
    { key: 'alInter', header: 'Interestadual', numeric: true, render: (row) => <Percent value={row.alInter} /> },
    { key: 'alDest', header: 'Interna do destino', numeric: true, render: (row) => <Percent value={row.alDest} /> },
    { key: 'fcp', header: 'FCP', numeric: true, render: (row) => <Percent value={row.fcp} /> },
    {
      key: 'st',
      header: 'ST',
      render: (row) => (row.st ? <Badge variant="neutral">sujeito a ST</Badge> : <span className="faint">—</span>),
    },
  ];

  if (isAdmin) {
    columns.push({
      key: 'actions',
      header: 'Ações',
      render: (row) => (
        <div className="row row-end row-wrap">
          <Button size="sm" variant="ghost" onClick={() => setModalState(row)}>
            Editar
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(row)}>
            Excluir
          </Button>
        </div>
      ),
    });
  }

  return (
    <>
      <PageHeader
        title="Estados"
        meta={
          isAdmin
            ? CONSEQUENCE_NOTE
            : 'Alíquotas de todos os estados, usadas no cálculo das propostas. Somente administradores podem editar — esta tela é de leitura para você.'
        }
        actions={isAdmin ? <Button onClick={() => setModalState('new')}>Novo estado</Button> : undefined}
      />

      <Panel>
        <DataTable
          columns={columns}
          rows={statesQuery.data ?? []}
          rowKey={(row) => row.uf}
          loading={statesQuery.isLoading}
          empty={<EmptyState title="Nenhum estado cadastrado." description="Cadastre o primeiro estado." />}
        />
      </Panel>

      {modalState ? (
        <StateFormModal
          initial={modalState === 'new' ? null : modalState}
          existingUfs={(statesQuery.data ?? []).map((s) => s.uf)}
          onClose={() => setModalState(null)}
          onCreate={(input) => createState.mutateAsync(input)}
          onUpdate={(uf, input) => updateState.mutateAsync({ uf, input })}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Excluir estado"
        tone="danger"
        confirmLabel="Excluir"
        loading={deleteBusy}
        message={
          deleteTarget ? (
            <>
              Excluir <strong>{deleteTarget.nome}</strong> ({deleteTarget.uf})? Isso só é possível se não
              houver clientes cadastrados nesse estado.
            </>
          ) : (
            ''
          )
        }
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

function StateFormModal({
  initial,
  existingUfs,
  onClose,
  onCreate,
  onUpdate,
}: {
  initial: StateTax | null;
  existingUfs: string[];
  onClose: () => void;
  // O modal só aguarda a gravação; o registro devolvido não é usado aqui.
  onCreate: (input: CreateStateInput) => Promise<unknown>;
  onUpdate: (uf: string, input: UpdateStateInput) => Promise<unknown>;
}) {
  const toast = useToast();
  const isEdit = initial !== null;
  const [uf, setUf] = useState(initial?.uf ?? '');
  const [nome, setNome] = useState(initial?.nome ?? '');
  const [alInter, setAlInter] = useState(initial ? fractionToPercentInput(initial.alInter) : '');
  const [alDest, setAlDest] = useState(initial ? fractionToPercentInput(initial.alDest) : '');
  const [fcp, setFcp] = useState(initial ? fractionToPercentInput(initial.fcp) : '0,00');
  const [st, setSt] = useState(initial?.st ?? false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const ufNormalized = uf.trim().toUpperCase();
    if (!isEdit) {
      if (ufNormalized.length !== 2) {
        setFieldErrors({ uf: 'A UF tem 2 letras.' });
        return;
      }
      if (existingUfs.includes(ufNormalized)) {
        setFieldErrors({ uf: 'Já existe um estado com essa UF.' });
        return;
      }
    }

    setSaving(true);
    try {
      if (initial) {
        await onUpdate(initial.uf, {
          nome,
          st,
          fcp: percentInputToFraction(fcp),
          alInter: percentInputToFraction(alInter),
          alDest: percentInputToFraction(alDest),
        });
        toast.success('Estado atualizado.');
      } else {
        await onCreate({
          uf: ufNormalized,
          nome,
          st,
          fcp: percentInputToFraction(fcp),
          alInter: percentInputToFraction(alInter),
          alDest: percentInputToFraction(alDest),
        });
        toast.success('Estado criado.');
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível salvar agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      title={initial ? `Editar ${initial.nome}` : 'Novo estado'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="state-form" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id="state-form" className="stack" onSubmit={handleSubmit} noValidate>
        <p className="muted">{CONSEQUENCE_NOTE}</p>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="field-grid">
          <Field
            label="Nome"
            required
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            error={fieldErrors.nome}
          />
          <Field
            label="UF"
            required
            maxLength={2}
            wrapperClassName="uf-input"
            disabled={isEdit}
            value={uf}
            onChange={(e) => setUf(e.target.value.toUpperCase())}
            error={fieldErrors.uf}
          />
        </div>
        <div className="row row-wrap">
          <Field
            label="Alíquota interestadual (%)"
            required
            inputMode="decimal"
            wrapperClassName="rate-input"
            value={alInter}
            onChange={(e) => setAlInter(e.target.value)}
            error={fieldErrors.alInter}
          />
          <Field
            label="Alíquota interna do destino (%)"
            required
            inputMode="decimal"
            wrapperClassName="rate-input"
            value={alDest}
            onChange={(e) => setAlDest(e.target.value)}
            error={fieldErrors.alDest}
          />
          <Field
            label="FCP (%)"
            inputMode="decimal"
            wrapperClassName="rate-input"
            value={fcp}
            onChange={(e) => setFcp(e.target.value)}
            error={fieldErrors.fcp}
          />
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={st} onChange={(e) => setSt(e.target.checked)} />
          Sujeito a substituição tributária (ST)
        </label>
      </form>
    </Modal>
  );
}
